import React, {useCallback, useMemo, useState} from "react";
import {DefaultPaginationInfo, MarkProductForCacheSectionNames} from "../../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import Button from "../../../../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import {Col, Row} from "reactstrap";
import {Divider, Paper} from "@mui/material";
import SearchInput from "../../../../../../../components/base/input/search_input";
import ProductSelectionDialog from "../../../../../../../components/dialogs/product-selection";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import {TagConnectedPartsDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import useSearch from "../../../../../../../hooks/use-search";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";

const dataGridSaveConfig = {
    name: 'TagConnectedPartsDataGrid',
    version: 1,
}

const TagConnectedParts = ({tagId, setTagInfo, industry}) => {
    const [data, setData] = useState([]);
    const [openAddPartDialog, setOpenAddPartDialog] = useState(false);
    const isMounted = useIsMounted();

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: 'Part Number',
                name: TagConnectedPartsDataGridColumnNames.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: 'Description',
                name: TagConnectedPartsDataGridColumnNames.description,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: {size: 250, minWidth: 250, type: DataGridColumnWidthTypes.default},
            },
            {
                name: TagConnectedPartsDataGridColumnNames.deleteButton,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 200
            },
        ], [])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    /**
     * Searches over the connected parts to the tag,
     * - Appends isDeleted variable to the objects to distinguish if the connection is deleted or not
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const search = useCallback(async (args) => {
        const forApi = {
            tagId: tagId,
            keyword: args.filters?.keyword?.toString(),
            paginationDs: args.paginationInfo,
        };
        const response = await Api.getTagConnectedParts(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const _data = response?.data?.items?.map(e => ({
                ...e,
                isDeleted: false,
                loading: false,
            })) ?? [];
            setData(_data);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
            }))
        }
    }, [isMounted, tagId])

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Searches over the connected parts to a tag:
     * Adds the received keyword to the query params
     * @type {(function(*=): void)}
     */
    const searchOverConnectedParts = useCallback((keyword) => {
        keyword = keyword?.toString()?.trim();
        searchData.setFilters({
            keyword: keyword,
        })
    }, [searchData])

    /**
     * Handles click on connect part button:
     * Opens dialog for adding part to connections
     * @type {function(): void}
     */
    const onAddPartClicked = useCallback(() => {
        setOpenAddPartDialog(true);
    }, [])

    /**
     * Reconnects a part to the tag again same as adding connection:
     * - Calls the api regarding this action
     * @type {(function(*=): Promise<void>)}
     */
    const onReconnectAgain = useCallback(async (connectedPart) => {
        const forApi = {
            partId: connectedPart.partId,
            tagId: parseInt(tagId)
        }
        setData(prevState => prevState.map(e => e.partId === connectedPart.partId ? {...e, loading: true} : e));
        const response = await Api.addTagToOperatorPart(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.partId === connectedPart.partId ? {
                ...e,
                loading: false,
                isDeleted: false
            } : e))
            setTagInfo(prevState => ({...prevState, connectedPartCount: prevState.connectedPartCount + 1}))
        } else {
            setData(prevState => prevState.map(e => e.partId === connectedPart.partId ? {...e, loading: false} : e));
        }
    }, [isMounted, setTagInfo, tagId])

    /**
     * Deletes the connection to the tag
     * - Calls the api regarding this action
     * @param connectedPart
     */
    const onDeleteConnection = useCallback(async (connectedPart) => {
        const forApi = {
            partId: connectedPart.partId,
            tagId: parseInt(tagId)
        }
        setData(prevState => prevState.map(e => e.partId === connectedPart.partId ? {...e, loading: true} : e));
        const response = await Api.removeTagFromOperatorPart(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.partId === connectedPart.partId ? {
                ...e,
                loading: false,
                isDeleted: true
            } : e))
            setTagInfo(prevState => ({...prevState, connectedPartCount: prevState.connectedPartCount - 1}))
        } else {
            setData(prevState => prevState.map(e => e.partId === connectedPart.partId ? {...e, loading: false} : e));
        }
    }, [isMounted, setTagInfo, tagId])

    /**
     *  Handles adding new part to the system:
     */
    const onNewPartSelected = useCallback(async (part) => {
        const forApi = {
            tagId: tagId,
            partId: part.id,
        }
        const response = await Api.addTagToOperatorPart(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => [
                {
                    partId: part.id,
                    partNumber: part?.partNo ?? part?.partNumber,
                    partDescription: part.description,
                    isDeleted: false,
                    loading: false,
                }, ...prevState])
            setTagInfo(prevState => ({...prevState, connectedPartCount: prevState.connectedPartCount + 1}))

            void Api.markPartsForCache({partIds: [part.id], sectionNames: [MarkProductForCacheSectionNames.tag]});
        }
        return response?.resultFlag;
    }, [isMounted, setTagInfo, tagId])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data.map(e => ({
            key: e.partId,
            data: e,
            cells: {
                [TagConnectedPartsDataGridColumnNames.partNumber]:
                    <ProductInformationPopover partNo={e.partNumber}>
                        {e.partNumber ?? '--'}
                    </ProductInformationPopover>,
                [TagConnectedPartsDataGridColumnNames.description]:
                    <div dangerouslySetInnerHTML={{__html: e.partDescription}}/>,
                [TagConnectedPartsDataGridColumnNames.deleteButton]:
                    e.isDeleted
                        ? <Button
                            variant={'contained'}
                            color={'primary'}
                            disabled={e.loading}
                            onClick={() => onReconnectAgain(e)}
                        >
                            Reconnect Again
                        </Button>
                        : <Button
                            variant={'outlined'}
                            color={'primary'}
                            disabled={e.loading}
                            onClick={() => onDeleteConnection(e)}
                        >
                            Delete Connection
                        </Button>,

            },
        }))
    }, [data, onDeleteConnection, onReconnectAgain])

    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center surveys-list-view'>
                <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 px-3'}>
                    <div/>
                    <Button
                        className='d-flex justify-content-center align-items-center'
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddPartClicked}
                    >
                        <Add className='icon normal p-1 mr-2'/>
                        <p className={'button-label'}>Connect a Product</p>
                    </Button>
                </div>
                <Paper className='paper w-100 px-3 pt-2 mb-5 flex-wrap justify-content-between align-items-start'>
                    <Row className='no-gutters align-items-center justify-content-between w-100 '>
                        <Col xs={12}>
                            <SearchInput
                                loading={searchData.loading}
                                autoSearch
                                immediateSearch
                                initialValue={searchData.filters?.keyword ?? ''}
                                search={searchOverConnectedParts}
                                label={'Search'}
                            />
                        </Col>
                        <Divider className='divider'/>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            initialLayoutState={{columns: columns}}
                            rows={dataGridRows}
                            columns={dataGridColumns}
                            hideExporting
                            hideFooterActions
                            state={{
                                loading: {state: searchData.loading},
                                pagination: searchData.paginationInfo,
                                density: dataGridDensity,
                            }}
                            onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                            onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        />
                    </Row>
                </Paper>
            </Row>
            <ProductSelectionDialog
                onSelect={onNewPartSelected}
                existingProductIds={data?.map(e => e.partId) ?? []}
                industry={industry}
                close={() => setOpenAddPartDialog(false)}
                open={openAddPartDialog}
            />
        </>
    );
}

export default TagConnectedParts;
